import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet, apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H5, ProgressBar } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Eye, User, Briefcase, FileText } from 'react-feather';
import { Modal, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { mediaUrl } from "../../Utils"; // Ensure mediaUrl is imported
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

// Define Interface for Driver
interface DriverData {
    id: number;
    name: string;
    email: string; // Added
    phone_number: string;
    status: string;
    gender: string; // Added
    date_of_birth: string; // Added
    address: string; // Added
    blood_group: string; // Added
    nationality: string; // Added
    license_number: string;
    license_expiry_date: string; // Added
    joining_date: string; // Added
    years_of_experience: number;
    assigned_branch_id: string | number; // Updated type
    license_image_front: string; // Added
    license_image_back: string; // Added
    branch?: { branch_name: string; deleted_at?: string };
    designation?: string;
    driver_image?: string;
}

export default function ViewDriver() {
    const [data, setData] = useState<DriverData[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const prevSearch = React.useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [modal, setModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState<DriverData | null>(null);
    const [activeTab, setActiveTab] = useState('1');

    const toggleModal = () => {
        if (modal) {
            handleClose();
        } else {
            setModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setModal(false);
            setIsClosing(false);
        }, 550);
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const handleView = (driver: DriverData) => {
        setSelectedDriver(driver);
        setActiveTab('1'); // Reset to first tab
        setModal(true);
    };

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        // Reset to page 1 when search changes
        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return; // ← KEY FIX: Exit early, let page change trigger next fetch
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);

        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                // console.log("Sending sort params:", sortCol, sortDir);
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("drivers/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total || 0);
            } else {
                setData(response);
                setTotalRows(response.length || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        // setLoading(true);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        // setLoading(true);
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        // setCurrentPage(1);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_driver`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_driver`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("drivers/delete", { id: id });
                    toast.success("Driver deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete driver", { autoClose: 1000 });
                }
            }
        });
    };

    const calculateProfileCompletion = (row: DriverData) => {
        const mandatoryFields = [
            'name', 'email', 'phone_number', 'status', 'gender', 'date_of_birth',
            'address', 'blood_group', 'nationality', 'license_number',
            'license_expiry_date', 'joining_date', 'years_of_experience',
            'assigned_branch_id', 'license_image_front', 'license_image_back'
        ];
        let filledCount = 0;
        mandatoryFields.forEach(field => {
            if (row[field as keyof DriverData]) {
                filledCount++;
            }
        });
        return Math.min(Math.round((filledCount / mandatoryFields.length) * 100), 100);
    };

    const columns = [
        {
            name: <div>Sl. No</div>,
            selector: (row: DriverData, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: <div>Name</div>,
            selector: (row: DriverData) => row.name,
            sortable: true,
            sortField: "name",
            wrap: true,
        },
        {
            name: <div>Phone</div>,
            selector: (row: DriverData) => row.phone_number,
            sortable: true,
            sortField: "phone_number",
            wrap: true,
        },
        {
            name: <div>License No</div>,
            selector: (row: DriverData) => row.license_number,
            sortable: true,
            sortField: "license_number",
            wrap: true,
        },
        {
            name: <div>Branch</div>,
            cell: (row: DriverData) => (
                <div>
                    {row.branch?.branch_name || row.assigned_branch_id || "N/A"}
                    {row.branch?.deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </div>
            ),
            sortable: true,
            sortField: "branch_name",
            wrap: true,
        },
        {
            name: <div>Profile Status</div>,
            cell: (row: DriverData) => {
                const completion = calculateProfileCompletion(row);
                return (
                    <div className="w-100">
                        <div className="progress-bar-animated progress-bar-striped">
                            <ProgressBar
                                value={completion}
                                color={completion === 100 ? 'success' : 'warning'}
                                striped={completion <= 100}
                                animated={completion < 100}
                                style={{ height: '8px' }}
                            />
                        </div>
                        <div className="text-center small mt-1">{completion}%</div>
                    </div>
                );
            },
            sortable: false,
            width: "150px"
        },
        {
            name: <div>Status</div>,
            cell: (row: DriverData) => (
                <span className={`badge ${row.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                    {row.status}
                </span>
            ),
            sortable: true,
            sortField: "status",
        },
        {
            name: <div>Actions</div>,
            cell: (row: DriverData) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('driver.view') && (
                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('driver.edit') && (
                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>)}
                    {hasPermission('driver.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1', // Light teal background
                color: '#00695c', // Dark teal text
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px', // override the row height
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs
                pageTitle="View Driver"
                parent="General"
                title="View Driver"
            />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Driver Overview</H3>
                                {hasPermission('driver.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Driver
                                    </Btn>
                                )}
                            </Col>
                            <>
                                <Col xs={12} className="mb-3 d-flex justify-content-end">
                                    <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                </Col>
                                <div className="table-responsive">
                                    <DataTable
                                        data={data}
                                        columns={columns}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onSort={handleSort}
                                        sortServer
                                        progressPending={loading}
                                        progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                        highlightOnHover
                                        customStyles={customStyles}
                                    />
                                </div>
                            </>
                        </CardBody>
                    </Card>
                </Col>
            </Container>

            {/* View Driver Modal */}
            <Modal isOpen={modal} toggle={toggleModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                <div style={{
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    padding: '1.5rem',
                    position: 'relative', width: '100%'
                }}>
                    <button
                        className="btn-close btn-close-white"
                        type="button"
                        onClick={handleClose}
                        style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                    ></button>

                    {selectedDriver && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                border: '3px solid rgba(255,255,255,0.3)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                                flexShrink: 0,
                                backgroundColor: '#f8f9fa',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                {/* Assuming driver_image might not be in the initial list API, we check if we have it or use placeholder */}
                                {/* The getall API likely doesn't return the full image URL but might if customized. 
                                    If keys are missing from the interface, typically they are still in the object at runtime. 
                                    I will try to use a property if it exists or fallback.
                                    The Interface doesn't define driver_image, let's assume it might be passed or add it later.
                                    For now, I'll use a placeholder if undefined.
                                */}
                                <img
                                    src={(selectedDriver as any).driver_image ? `${mediaUrl}${(selectedDriver as any).driver_image}` : 'https://via.placeholder.com/100/1e3c72/ffffff?text=Driver'}
                                    alt="Driver"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100/1e3c72/ffffff?text=Driver';
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.75rem',
                                    margin: 0,
                                    marginBottom: '0.5rem'
                                }}>
                                    {selectedDriver.name}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span>{selectedDriver.designation || 'Driver'}</span>
                                    <span>•</span>
                                    <span>{selectedDriver.phone_number}</span>
                                </div>
                            </div>
                            <div>
                                <span style={{
                                    backgroundColor: selectedDriver.status === 'Active' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255,255,255,0.2)',
                                    color: selectedDriver.status === 'Active' ? '#10b981' : 'white',
                                    padding: '0.6rem 1.2rem',
                                    borderRadius: '25px',
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    border: `2px solid ${selectedDriver.status === 'Active' ? '#10b981' : 'rgba(255,255,255,0.3)'}`,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    {selectedDriver.status}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <User size={16} /> Personal Info
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '2' ? 'active' : ''}
                                onClick={() => toggleTab('2')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Briefcase size={16} /> Professional Info
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '3' ? 'active' : ''}
                                onClick={() => toggleTab('3')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <FileText size={16} /> Documents
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedDriver && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Email</label>
                                                    <div className="fw-semibold">{selectedDriver.email || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Gender</label>
                                                    <div className="fw-semibold">{selectedDriver.gender || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Nationality</label>
                                                    <div className="fw-semibold">{selectedDriver.nationality || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Date of Birth</label>
                                                    <div className="fw-semibold">{selectedDriver.date_of_birth || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Blood Group</label>
                                                    <div className="fw-semibold">{selectedDriver.blood_group || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Address</label>
                                                    <div className="fw-semibold">{selectedDriver.address || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Assigned Branch</label>
                                                    <div className="fw-semibold text-primary">
                                                        {selectedDriver.branch?.branch_name || 'N/A'}
                                                        {selectedDriver.branch?.deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">License Number</label>
                                                    <div className="fw-semibold">{selectedDriver.license_number || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Years of Experience</label>
                                                    <div className="fw-semibold">{selectedDriver.years_of_experience ? `${selectedDriver.years_of_experience} Years` : 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Joining Date</label>
                                                    <div className="fw-semibold">{selectedDriver.joining_date || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">License Expiry Date</label>
                                                    <div className="fw-semibold ">{selectedDriver.license_expiry_date || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="3">
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <div className="bg-white p-3 rounded shadow-sm h-100">
                                                <h6 className="fw-bold mb-3 border-bottom pb-2">License Front</h6>
                                                <div className="text-center bg-light rounded p-2" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {selectedDriver.license_image_front ? (
                                                        <img
                                                            src={`${mediaUrl}${selectedDriver.license_image_front}`}
                                                            alt="License Front"
                                                            className="img-fluid rounded"
                                                            style={{ maxHeight: '250px', objectFit: 'contain' }}
                                                        />
                                                    ) : <span className="text-muted">No Image Available</span>}
                                                </div>
                                                {selectedDriver.license_image_front && (
                                                    <div className="mt-2 text-center">
                                                        <a href={`${mediaUrl}${selectedDriver.license_image_front}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                                            View Full Size
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="bg-white p-3 rounded shadow-sm h-100">
                                                <h6 className="fw-bold mb-3 border-bottom pb-2">License Back</h6>
                                                <div className="text-center bg-light rounded p-2 " style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {selectedDriver.license_image_back ? (
                                                        <img
                                                            src={`${mediaUrl}${selectedDriver.license_image_back}`}
                                                            alt="License Back"
                                                            className="img-fluid rounded"
                                                            style={{ maxHeight: '250px', objectFit: 'contain' }}
                                                        />
                                                    ) : <span className="text-muted">No Image Available</span>}
                                                </div>
                                                {selectedDriver.license_image_back && (
                                                    <div className="mt-2 text-center">
                                                        <a href={`${mediaUrl}${selectedDriver.license_image_back}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                                            View Full Size
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>
            <ToastContainer autoClose={1000} />
        </div>
    );
}
